import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  addStockToProduct, 
  createProduct, 
  addProductBarcode as _addProductBarcode,
  updateProduct,
  lookupExternalBarcode,
  ExternalBarcodeLookupResponse as _ExternalBarcodeLookupResponse,
  CreateProductRequest 
} from 'utils/grocyApi.ts';
import { queryKeys } from './queryKeys.ts';
import { provideSuccessFeedback } from 'utils/vibration.ts';
import { toast } from 'react-hot-toast';

export function usePurchaseMutation() {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.stock });
    queryClient.invalidateQueries({ queryKey: queryKeys.products });
    queryClient.invalidateQueries({ queryKey: queryKeys.productBarcodes });
  };

  const purchaseExisting = useMutation({
    mutationFn: async ({ 
      productId, 
      amount, 
      expiryDate, 
      locationId,
      currentLocationId,
      parentProductId
    }: {
      productId: number;
      amount: number;
      expiryDate?: string;
      locationId?: number;
      currentLocationId?: number;
      parentProductId?: number;
    }) => {
      // Update product properties if needed
      const updateData: Partial<{ parent_product_id: number; location_id: number }> = {};
      
      if (parentProductId) {
        updateData.parent_product_id = parentProductId;
      }

      if (locationId && locationId !== currentLocationId) {
        updateData.location_id = locationId;
      }
      
      // Only update product if we have fields to update
      if (Object.keys(updateData).length > 0) {
        await updateProduct(productId, updateData);
      }

      // Add stock (location_id here is for where this stock is stored)
      await addStockToProduct(productId, {
        amount,
        best_before_date: expiryDate,
        location_id: locationId,
        transaction_type: 'purchase',
      });
    },
    onSuccess: () => {
      toast.success('Stock added successfully');
      provideSuccessFeedback();
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(`Purchase failed: ${error.message}`);
    },
  });

  const purchaseNew = useMutation({
    mutationFn: async ({
      barcode,
      amount,
      expiryDate,
      locationId,
      productGroupId,
      parentProductId,
    }: {
      barcode: string;
      amount: number;
      expiryDate?: string;
      locationId: number;
      productGroupId: number;
      parentProductId?: number;
    }) => {
      // Use server-side product creation with add=true (barcode is already normalized)
      const result = await lookupExternalBarcode(barcode, true);
      
      if (!result || !result.id) {
        throw new Error('Failed to create product on server');
      }

      const productId = result.id;

      // Update the created product with our custom fields
      const updateData: Partial<{ product_group_id: number; parent_product_id: number; location_id: number }> = {};
      
      if (productGroupId) {
        updateData.product_group_id = productGroupId;
      }
      
      if (parentProductId) {
        updateData.parent_product_id = parentProductId;
      }

      // Update product default location if different from server default
      if (locationId !== result.location_id) {
        updateData.location_id = locationId;
      }

      // Only update if we have fields to update
      if (Object.keys(updateData).length > 0) {
        await updateProduct(productId, updateData);
      }

      // Add stock (location_id here is for where this stock is stored)
      await addStockToProduct(productId, {
        amount,
        best_before_date: expiryDate,
        location_id: locationId,
        transaction_type: 'purchase',
      });
    },
    onSuccess: () => {
      toast.success('Product created and stock added');
      provideSuccessFeedback();
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(`Product creation failed: ${error.message}`);
    },
  });

  const createAndPurchase = useMutation({
    mutationFn: async ({
      productData,
      amount,
      expiryDate,
      locationId,
      parentProductId,
    }: {
      productData: CreateProductRequest & { no_own_stock?: boolean };
      amount: number;
      expiryDate?: string;
      locationId: number;
      parentProductId?: number;
    }) => {
      const finalProductData: CreateProductRequest = {
        ...productData,
        no_own_stock: productData.no_own_stock ? 1 : 0,
      } as CreateProductRequest;

      const createResponse = await createProduct(finalProductData);
      const productId = createResponse.created_object_id;

      const updateData: Partial<{ parent_product_id: number }> = {};
      if (parentProductId) updateData.parent_product_id = parentProductId;
      if (Object.keys(updateData).length > 0) {
        await updateProduct(productId, updateData);
      }

      await addStockToProduct(productId, {
        amount,
        best_before_date: expiryDate,
        location_id: locationId,
        transaction_type: 'purchase',
      });
    },
    onSuccess: () => {
      toast.success('Product created and stock added');
      provideSuccessFeedback();
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(`Create & purchase failed: ${error.message}`);
    },
  });

  // Link a scanned barcode to an existing product and purchase stock
  const linkBarcodeAndPurchase = useMutation({
    mutationFn: async ({
      productId,
      barcode,
      amount,
      expiryDate,
      locationId,
      currentLocationId,
    }: {
      productId: number;
      barcode: string;
      amount: number;
      expiryDate?: string;
      locationId: number;
      currentLocationId?: number;
    }) => {
      // First, attach the barcode to the selected product
      await _addProductBarcode(productId, barcode);

      if (locationId !== currentLocationId) {
        await updateProduct(productId, { location_id: locationId });
      }

      // Then purchase stock for that product
      await addStockToProduct(productId, {
        amount,
        best_before_date: expiryDate,
        location_id: locationId,
        transaction_type: 'purchase',
      });
    },
    onSuccess: () => {
      toast.success('Barcode linked and stock added');
      provideSuccessFeedback();
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(`Link & purchase failed: ${error.message}`);
    },
  });

  return {
    purchaseExisting: purchaseExisting.mutateAsync,
    purchaseNew: purchaseNew.mutateAsync,
    createAndPurchase: createAndPurchase.mutateAsync,
    linkBarcodeAndPurchase: linkBarcodeAndPurchase.mutateAsync,
    isLoading: purchaseExisting.isPending || purchaseNew.isPending || createAndPurchase.isPending,
  };
} 
